import {
  objectiveProgress,
  type BuildingTileKind,
  type GameState,
  type PlayerId,
} from "@dailywars/core/engine";

import { getBuildingIconSrc } from "#/game/buildingSprites";
import { cn } from "#/lib/utils";

function ObjectiveBuildingIcon({
  playerId,
  buildingKind,
}: {
  playerId: PlayerId;
  buildingKind: BuildingTileKind;
}) {
  return (
    <img
      src={getBuildingIconSrc(buildingKind, playerId)}
      alt=""
      aria-hidden
      className="size-5 shrink-0 sm:size-6"
      draggable={false}
    />
  );
}

function ObjectiveCount({
  playerId,
  buildingKind,
  count,
  target,
  align,
}: {
  playerId: PlayerId;
  buildingKind: BuildingTileKind;
  count: number;
  target: number;
  align: "left" | "right";
}) {
  const icon = <ObjectiveBuildingIcon playerId={playerId} buildingKind={buildingKind} />;
  const value = (
    <span>
      {count}/{target}
    </span>
  );
  return (
    <span
      aria-label={`${buildingKind} ${count} of ${target}`}
      className={cn(
        "inline-flex min-w-0 flex-1 items-center gap-1 tabular-nums text-foreground",
        align === "left" ? "justify-start" : "justify-end",
      )}
    >
      {align === "left" ? (
        <>
          {icon}
          {value}
        </>
      ) : (
        <>
          {value}
          {icon}
        </>
      )}
    </span>
  );
}

export function ObjectiveStatusBar({ state }: { state: GameState }) {
  const progress = objectiveProgress(state);
  const buildingKind: BuildingTileKind | null =
    progress.kind === "capture" ? "city" : progress.kind === "monuments" ? "monument" : null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-9 items-center justify-between gap-2 border-t border-border px-3 text-xs sm:px-5"
    >
      {buildingKind && progress.target !== null ? (
        <ObjectiveCount
          playerId="p1"
          buildingKind={buildingKind}
          count={progress.p1Count}
          target={progress.target}
          align="left"
        />
      ) : (
        <span className="flex-1" />
      )}
      <span className="shrink-0 text-center font-medium text-foreground">{progress.label}</span>
      {buildingKind && progress.target !== null ? (
        <ObjectiveCount
          playerId="p2"
          buildingKind={buildingKind}
          count={progress.p2Count}
          target={progress.target}
          align="right"
        />
      ) : (
        <span className="flex-1" />
      )}
    </div>
  );
}
